import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import {
  Loader2, Plus, Archive, ArchiveRestore, Users, Building2, Mail,
  Send, Trash2, HardHat, Search, Pencil, Check, X, ListChecks,
} from 'lucide-react';
import { BCCaptureView } from '../../pages/portal/BuildingControlDetail';
import { BCStageTracker } from '../BCStageTracker';

const SUBS = [
  ['contacts', 'Contacts', Mail],
  ['projects', 'Project contacts', Building2],
  ['access', 'User access', Users],
  ['queue', 'Send queue', Send],
  ['log', 'All captures', HardHat],
  ['progress', 'Progress', ListChecks],
];

export function BuildingControlTab({ api }) {
  const [sub, setSub] = useState('contacts');
  const [settings, setSettings] = useState({ office_cc_email: '', filing_live: false, sending_live: false });
  const [cc, setCc] = useState('');
  const [savingCc, setSavingCc] = useState(false);

  const [contacts, setContacts] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const [projects, setProjects] = useState([]);
  const [projQ, setProjQ] = useState('');
  const [users, setUsers] = useState([]);
  const [queue, setQueue] = useState([]);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState(null);
  const [trackerProjectId, setTrackerProjectId] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, p, u, q, l] = await Promise.all([
        api.get('/bc/settings'),
        api.get(`/bc/contacts?include_archived=true`),
        api.get('/bc/projects'),
        api.get('/bc/users'),
        api.get('/bc/queue'),
        api.get('/bc/captures?scope=all'),
      ]);
      setSettings(s.data); setCc(s.data.office_cc_email || '');
      setContacts(c.data || []); setProjects(p.data || []); setUsers(u.data || []);
      setQueue(q.data || []); setLog(l.data || []);
    } catch { toast.error('Failed to load Building Control settings'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const activeContacts = contacts.filter(c => !c.archived);

  const saveCc = async () => {
    setSavingCc(true);
    try { const r = await api.put('/bc/settings', { office_cc_email: cc.trim() }); setSettings(r.data); toast.success('Office CC saved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSavingCc(false); }
  };

  const addContact = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error('Name and email are required'); return; }
    try { await api.post('/bc/contacts', form); setForm({ name: '', email: '', phone: '', notes: '' }); toast.success('Contact added'); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Add failed'); }
  };
  const saveEdit = async (id) => {
    try { await api.patch(`/bc/contacts/${id}`, editForm); setEditId(null); toast.success('Contact updated'); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };
  const toggleArchive = async (c) => {
    try { await api.post(`/bc/contacts/${c.id}/${c.archived ? 'unarchive' : 'archive'}`); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const assignContact = async (projectId, contactId) => {
    try { await api.put(`/bc/projects/${projectId}/contact`, { contact_id: contactId === 'none' ? null : contactId }); toast.success('Project contact updated'); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const toggleUser = async (u, enabled) => {
    try { await api.put(`/bc/users/${u.id}/toggle`, { enabled }); setUsers(prev => prev.map(x => x.id === u.id ? { ...x, bc_capture_enabled: enabled } : x)); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const queueSend = async (id) => {
    try { await api.post(`/bc/queue/${id}/send`); toast.success('Sent'); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
  };
  const queueDiscard = async (id) => {
    try { await api.post(`/bc/queue/${id}/discard`); toast.success('Discarded'); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const filteredProjects = projects.filter(p => `${p.project_ref} ${p.client_name} ${p.site_address}`.toLowerCase().includes(projQ.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="bc-settings-tab">
      {/* status banner */}
      <Card className="bg-[#2A3240] border-white/10">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <HardHat className="w-5 h-5 text-[#F5A623]" />
          <span className="text-white font-semibold text-sm">Building Control</span>
          <Badge className={`text-[10px] ${settings.filing_live ? 'bg-green-500/20 text-green-300' : 'bg-amber-500/20 text-amber-300'}`}>Filing: {settings.filing_live ? 'live' : 'preview (off)'}</Badge>
          <Badge className={`text-[10px] ${settings.sending_live ? 'bg-green-500/20 text-green-300' : 'bg-amber-500/20 text-amber-300'}`}>Email: {settings.sending_live ? 'live' : 'not live'}</Badge>
          <div className="ml-auto flex items-center gap-2">
            <Mail className="w-4 h-4 text-gray-400" />
            <Input value={cc} onChange={e => setCc(e.target.value)} placeholder="Office CC email (optional)" className="w-56 bg-[#1E2530] border-white/10 text-white text-sm h-9" data-testid="bc-office-cc" />
            <Button onClick={saveCc} disabled={savingCc} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] h-9" data-testid="bc-office-cc-save">{savingCc ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}</Button>
          </div>
        </CardContent>
      </Card>

      {/* sub nav */}
      <div className="flex flex-wrap gap-2">
        {SUBS.map(([key, label, Icon]) => (
          <Button key={key} variant={sub === key ? 'default' : 'ghost'} onClick={() => setSub(key)}
            className={sub === key ? 'bg-[#F5A623] text-[#1E2530]' : 'text-gray-400 hover:text-white'} data-testid={`bc-sub-${key}`}>
            <Icon className="w-4 h-4 mr-2" />{label}{key === 'queue' && queue.length > 0 ? ` (${queue.length})` : ''}
          </Button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#F5A623]" /></div> : (
        <>
          {/* CONTACTS */}
          {sub === 'contacts' && (
            <div className="space-y-3">
              <Card className="bg-[#2A3240] border-white/10">
                <CardHeader className="pb-2"><CardTitle className="text-white text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> Add a Building Control contact</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-2">
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Name / body" className="bg-[#1E2530] border-white/10 text-white text-sm" data-testid="bc-contact-name" />
                  <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Email" className="bg-[#1E2530] border-white/10 text-white text-sm" data-testid="bc-contact-email" />
                  <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" className="bg-[#1E2530] border-white/10 text-white text-sm" data-testid="bc-contact-phone" />
                  <Button onClick={addContact} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]" data-testid="bc-contact-add">Add</Button>
                </CardContent>
              </Card>
              <label className="flex items-center gap-2 text-xs text-gray-400"><input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} data-testid="bc-show-archived" /> Show archived</label>
              <div className="space-y-2">
                {contacts.filter(c => showArchived || !c.archived).map(c => (
                  <Card key={c.id} className="bg-[#2A3240] border-white/5" data-testid={`bc-contact-row-${c.id}`}>
                    <CardContent className="p-3">
                      {editId === c.id ? (
                        <div className="flex flex-wrap gap-2 items-center">
                          <Input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} className="w-40 bg-[#1E2530] border-white/10 text-white text-sm h-8" />
                          <Input value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} className="w-56 bg-[#1E2530] border-white/10 text-white text-sm h-8" />
                          <Input value={editForm.phone || ''} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} placeholder="Phone" className="w-36 bg-[#1E2530] border-white/10 text-white text-sm h-8" />
                          <Button size="sm" onClick={() => saveEdit(c.id)} className="bg-emerald-600 text-white h-8"><Check className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditId(null)} className="text-gray-400 h-8"><X className="w-4 h-4" /></Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium">{c.name} {c.archived && <Badge className="ml-1 text-[10px] bg-white/10 text-gray-400">archived</Badge>}</p>
                            <p className="text-gray-400 text-xs">{c.email}{c.phone ? ` · ${c.phone}` : ''}</p>
                          </div>
                          <button onClick={() => { setEditId(c.id); setEditForm({ name: c.name, email: c.email, phone: c.phone || '' }); }} className="text-gray-400 hover:text-white" data-testid={`bc-contact-edit-${c.id}`}><Pencil className="w-4 h-4" /></button>
                          <button onClick={() => toggleArchive(c)} className="text-gray-400 hover:text-white" data-testid={`bc-contact-archive-${c.id}`}>{c.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}</button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
                {contacts.length === 0 && <p className="text-gray-500 text-sm">No contacts yet.</p>}
              </div>
            </div>
          )}

          {/* PROJECT CONTACTS */}
          {sub === 'projects' && (
            <div className="space-y-2">
              <div className="relative">
                <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input value={projQ} onChange={e => setProjQ(e.target.value)} placeholder="Search projects" className="pl-9 bg-[#1E2530] border-white/10 text-white text-sm" data-testid="bc-project-filter" />
              </div>
              {filteredProjects.map(p => (
                <Card key={p.id} className="bg-[#2A3240] border-white/5" data-testid={`bc-projrow-${p.id}`}>
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-[#F5A623] text-xs font-bold">{p.project_ref}</p>
                      <p className="text-gray-400 text-xs truncate">{p.client_name} · {p.site_address || 'No address'}{!p.has_folder && <span className="text-amber-400/70"> · no SharePoint folder</span>}</p>
                    </div>
                    <Select value={p.bc_contact_id || 'none'} onValueChange={v => assignContact(p.id, v)}>
                      <SelectTrigger className="w-52 bg-[#1E2530] border-white/10 text-white text-xs h-9" data-testid={`bc-project-contact-${p.id}`}><SelectValue placeholder="No contact" /></SelectTrigger>
                      <SelectContent className="bg-[#2A3240] border-white/10 max-h-64">
                        <SelectItem value="none" className="text-gray-400">No contact</SelectItem>
                        {activeContacts.map(c => <SelectItem key={c.id} value={c.id} className="text-white">{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* USER ACCESS */}
          {sub === 'access' && (
            <div className="space-y-2">
              <p className="text-gray-400 text-xs">Turn Building Control capture on per user. Off by default. Operatives, surveyors and sub-contractors can all Save; only surveyors and admins can Send.</p>
              {users.map(u => (
                <Card key={u.id} className="bg-[#2A3240] border-white/5" data-testid={`bc-user-row-${u.id}`}>
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm">{u.name || u.email}</p>
                      <p className="text-gray-500 text-xs">{u.email} · {(u.roles || []).join(', ') || u.role}</p>
                    </div>
                    <button onClick={() => toggleUser(u, !u.bc_capture_enabled)} className={`relative w-11 h-6 rounded-full transition-colors ${u.bc_capture_enabled ? 'bg-emerald-500' : 'bg-white/15'}`} data-testid={`bc-user-toggle-${u.id}`}>
                      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${u.bc_capture_enabled ? 'left-5' : 'left-0.5'}`} />
                    </button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* QUEUE */}
          {sub === 'queue' && (
            <div className="space-y-2">
              {!settings.sending_live && <p className="text-amber-400/80 text-xs">Email is not live yet — queued sends stay here until Resend is configured. Nothing is auto-sent.</p>}
              {queue.length === 0 ? <p className="text-gray-500 text-sm">Nothing queued.</p> : queue.map(c => (
                <Card key={c.id} className="bg-[#2A3240] border-white/5" data-testid={`bc-queue-row-${c.id}`}>
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm">{c.project_ref} · {c.stage}</p>
                      <p className="text-gray-400 text-xs">{c.recipient_email || 'no recipient'} · {c.photo_count} photo{c.photo_count !== 1 ? 's' : ''} · <span className={c.delivery === 'failed' ? 'text-red-400' : 'text-amber-400'}>{c.delivery}</span></p>
                    </div>
                    <Button size="sm" disabled={!settings.sending_live} onClick={() => queueSend(c.id)} className="bg-emerald-600 text-white hover:bg-emerald-500 h-8" data-testid={`bc-queue-send-${c.id}`}><Send className="w-3 h-3 mr-1" /> Send</Button>
                    <Button size="sm" variant="ghost" onClick={() => queueDiscard(c.id)} className="text-red-400 hover:bg-red-500/10 h-8" data-testid={`bc-queue-discard-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* LOG */}
          {sub === 'log' && (
            <div className="space-y-2">
              {log.length === 0 ? <p className="text-gray-500 text-sm">No captures yet.</p> : log.map(c => (
                <Card key={c.id} className="bg-[#2A3240] border-white/5 cursor-pointer hover:border-white/15" onClick={() => setDetailId(c.id)} data-testid={`bc-log-row-${c.id}`}>
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm">{c.project_ref} · {c.stage}</p>
                      <p className="text-gray-400 text-xs">{c.created_by_name} · {(c.created_at || '').slice(0, 16).replace('T', ' ')} · {c.photo_count} photo{c.photo_count !== 1 ? 's' : ''}</p>
                    </div>
                    <Badge className={`text-[10px] ${c.kind === 'send' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-white/10 text-gray-300'}`}>{c.kind === 'send' ? 'Sent' : 'Saved'}</Badge>
                    {c.in_error && <Badge className="text-[10px] bg-red-500/20 text-red-300">error</Badge>}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          {/* PROGRESS — per-project stage checklist */}
          {sub === 'progress' && (
            <div className="space-y-3">
              {!trackerProjectId ? (
                <>
                  <div className="relative">
                    <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input value={projQ} onChange={e => setProjQ(e.target.value)} placeholder="Search projects" className="pl-9 bg-[#1E2530] border-white/10 text-white text-sm" data-testid="bc-progress-filter" />
                  </div>
                  {filteredProjects.map(p => (
                    <button key={p.id} type="button" onClick={() => setTrackerProjectId(p.id)}
                      className="w-full text-left rounded-lg bg-[#2A3240] border border-white/5 hover:border-white/15 p-3 flex items-center gap-3 transition-colors"
                      data-testid={`bc-progress-project-${p.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-mono text-[#F5A623] text-xs font-bold">{p.project_ref}</p>
                        <p className="text-gray-400 text-xs truncate">{p.client_name} · {p.site_address || 'No address'}</p>
                      </div>
                      <ListChecks className="w-4 h-4 text-gray-500" />
                    </button>
                  ))}
                </>
              ) : (
                <>
                  <button onClick={() => setTrackerProjectId(null)} className="text-xs text-gray-400 hover:text-white flex items-center gap-1" data-testid="bc-progress-back">← All projects</button>
                  <Card className="bg-[#2A3240] border-white/10"><CardContent className="p-4">
                    <BCStageTracker projectId={trackerProjectId} variant="dark" />
                  </CardContent></Card>
                </>
              )}
            </div>
          )}
        </>
      )}

      <Dialog open={!!detailId} onOpenChange={o => !o && setDetailId(null)}>
        <DialogContent className="bg-[#1E2530] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">Building Control capture</DialogTitle></DialogHeader>
          {detailId && <BCCaptureView captureId={detailId} embedded onChanged={loadAll} onDeleted={() => setDetailId(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default BuildingControlTab;
